import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { IS_ADMIN_HOST } from "@/lib/auth";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { dashboardSeverity } from "@/lib/pqTuning";
import type { PqEvent, Window } from "@/lib/types";
import { useDynoWs } from "@/hooks/useDynoWs";
import { useEventQueue } from "@/stores/eventQueue";

interface PqListResponse {
  items: PqEvent[];
  total: number;
  limit: number;
  offset: number;
  window_id: string | null;
}

const DECISION_STYLES: Record<string, string> = {
  keep: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  promoted: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30",
  drop: "bg-amber-500/15 text-amber-400 border-amber-500/30",
  discard_non_swell: "bg-muted text-muted-foreground border-border",
};

function DecisionBadge({ decision }: { decision: string }) {
  const label =
    decision === "discard_non_swell"
      ? "non-swell"
      : decision === "promoted"
      ? "promoted"
      : decision;
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-2 py-0.5 text-xs font-medium",
        DECISION_STYLES[decision] ?? "bg-muted text-muted-foreground border-border"
      )}
    >
      {label}
    </span>
  );
}

function SeverityBadge({ severity }: { severity: string | null }) {
  if (!severity) return <span className="text-muted-foreground text-xs">—</span>;
  // Tone keys off the translated dashboard severity so both DB vocabularies
  // ('critical'/'major' from COMTRADE, 'severe'/'moderate' from fastlog)
  // render with the right urgency; the raw value stays as the label.
  const dash = dashboardSeverity(severity);
  const tone =
    dash === "severe"
      ? "text-destructive"
      : dash === "moderate"
      ? "text-amber-400"
      : "text-muted-foreground";
  return <span className={cn("text-xs font-medium uppercase", tone)}>{severity}</span>;
}

function formatTs(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

// Group rows by (service|system) + type + decision so a flurry of similar
// events (e.g. synthesizer drip or a misbehaving meter) collapses to one
// line with a count + latest-ts.
interface EventGroup {
  key: string;
  count: number;
  latest: PqEvent;
  members: PqEvent[];  // sorted newest-first
}

function groupEvents(events: PqEvent[]): EventGroup[] {
  const byKey = new Map<string, EventGroup>();
  for (const e of events) {
    const k = [
      e.service_name ?? e.system_id,
      e.event_type ?? "",
      e.decision ?? "keep",
    ].join("|");
    const existing = byKey.get(k);
    if (!existing) {
      byKey.set(k, { key: k, count: 1, latest: e, members: [e] });
    } else {
      existing.count += 1;
      existing.members.push(e);
      // events array is already sorted newest-first by caller; first wins.
      if ((e.trigger_ts ?? "") > (existing.latest.trigger_ts ?? "")) {
        existing.latest = e;
      }
    }
  }
  return Array.from(byKey.values());
}

// --- Column sorting -------------------------------------------------------
type SortKey = "ts" | "service" | "type" | "decision" | "severity" | "metric";
type SortDir = "asc" | "desc";
interface SortState {
  key: SortKey;
  dir: SortDir;
}

const SORT_KEYS: readonly SortKey[] = [
  "ts", "service", "type", "decision", "severity", "metric",
];
const DEFAULT_SORT: SortState = { key: "ts", dir: "desc" };

function parseSortFromParams(sp: URLSearchParams): SortState {
  const rawKey = sp.get("sort_key");
  const rawDir = sp.get("sort_dir");
  const key = (SORT_KEYS as readonly string[]).includes(rawKey ?? "")
    ? (rawKey as SortKey)
    : DEFAULT_SORT.key;
  const dir: SortDir = rawDir === "asc" ? "asc" : "desc";
  return { key, dir };
}

// Severity is ordinal, not alphabetical — rank so sort is meaningful.
// Keyed on the translated dashboard vocabulary (see lib/pqTuning.ts) so
// fastlog-vocab rows ('severe'/'moderate') rank correctly too.
const SEVERITY_RANK: Record<string, number> = { severe: 3, moderate: 2, minor: 1 };

function eventSortValue(e: PqEvent, key: SortKey): string | number {
  switch (key) {
    case "ts":
      return e.trigger_ts ?? "";
    case "service":
      return (e.service_name ?? e.system_id ?? "").toLowerCase();
    case "type":
      return (e.event_type ?? "").toLowerCase();
    case "decision":
      return (e.decision ?? "keep").toLowerCase();
    case "severity":
      return SEVERITY_RANK[dashboardSeverity(e.severity)] ?? 0;
    case "metric":
      return e.depth_pct ?? e.magnitude_pct ?? -Infinity;
  }
}

function compareEvents(a: PqEvent, b: PqEvent, sort: SortState): number {
  const av = eventSortValue(a, sort.key);
  const bv = eventSortValue(b, sort.key);
  let c: number;
  if (typeof av === "number" && typeof bv === "number") {
    c = av - bv;
  } else {
    c = String(av).localeCompare(String(bv));
  }
  // Stable tiebreak on trigger_ts (newest first) so equal keys don't jitter.
  if (c === 0) {
    c = (a.trigger_ts ?? "").localeCompare(b.trigger_ts ?? "");
  }
  return sort.dir === "asc" ? c : -c;
}

// Numeric/ordinal columns default to descending (biggest first); text columns
// default to ascending. Clicking the active column flips direction.
function nextSort(prev: SortState, key: SortKey): SortState {
  if (prev.key === key) {
    return { key, dir: prev.dir === "asc" ? "desc" : "asc" };
  }
  const numericDefault = key === "ts" || key === "metric" || key === "severity";
  return { key, dir: numericDefault ? "desc" : "asc" };
}

function SortHeader({
  label,
  sortKey,
  sort,
  onSort,
  className,
}: {
  label: string;
  sortKey: SortKey;
  sort: SortState;
  onSort: (k: SortKey) => void;
  className?: string;
}) {
  const active = sort.key === sortKey;
  return (
    <TableHead className={className}>
      <button
        type="button"
        onClick={() => onSort(sortKey)}
        className={cn(
          "inline-flex items-center gap-1 hover:text-foreground transition-colors",
          active ? "text-foreground" : "text-muted-foreground"
        )}
        aria-sort={active ? (sort.dir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        <span className="text-[9px] leading-none">
          {active ? (sort.dir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </TableHead>
  );
}

export function EventFeed() {
  const [sp, setSp] = useSearchParams();
  const windowId = sp.get("window");
  const navigate = useNavigate();

  // Open WS so pq_event_keep / pq_event_drop land in the eventQueue store.
  useDynoWs();
  const liveEventsMap = useEventQueue((s) => s.events);

  const [windows, setWindows] = useState<Window[]>([]);
  const [items, setItems] = useState<PqEvent[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [collapse, setCollapse] = useState(true);
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [deleting, setDeleting] = useState(false);
  // Sort state lives in URL search params so back-navigation from a PQ
  // event detail page restores the operator's prior sort (history pop
  // brings back the URL, which seeds the sort). Falls back to ts/desc
  // when params are missing.
  const sort = useMemo(() => parseSortFromParams(sp), [sp]);
  const handleSort = (k: SortKey) => {
    const next = nextSort(sort, k);
    const sp2 = new URLSearchParams(sp);
    sp2.set("sort_key", next.key);
    sp2.set("sort_dir", next.dir);
    setSp(sp2, { replace: true });
  };

  function toggleSelect(eventId: string) {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  }

  function toggleSelectGroup(g: EventGroup) {
    setSelected((s) => {
      const next = new Set(s);
      const allSelected = g.members.every((m) => next.has(m.event_id));
      if (allSelected) {
        for (const m of g.members) next.delete(m.event_id);
      } else {
        for (const m of g.members) next.add(m.event_id);
      }
      return next;
    });
  }

  async function deleteSelected() {
    if (selected.size === 0) return;
    if (!window.confirm(`Delete ${selected.size} event${selected.size === 1 ? "" : "s"}? This also removes the on-disk COMTRADE files.`)) {
      return;
    }
    setDeleting(true);
    try {
      const ids = Array.from(selected);
      await api.post("/api/pq/events/delete", { event_ids: ids });
      setItems((prev) => prev.filter((e) => !selected.has(e.event_id)));
      setSelected(new Set());
    } catch (e) {
      alert(`Delete failed: ${e}`);
    } finally {
      setDeleting(false);
    }
  }

  async function deleteOne(eventId: string) {
    if (!window.confirm("Delete this event?")) return;
    setDeleting(true);
    try {
      await api.delete(`/api/pq/events/${encodeURIComponent(eventId)}`);
      setItems((prev) => prev.filter((e) => e.event_id !== eventId));
      setSelected((s) => {
        const next = new Set(s);
        next.delete(eventId);
        return next;
      });
    } catch (e) {
      alert(`Delete failed: ${e}`);
    } finally {
      setDeleting(false);
    }
  }

  async function deleteGroup(g: EventGroup) {
    if (!window.confirm(`Delete all ${g.count} events in this group? This also removes the on-disk COMTRADE files.`)) {
      return;
    }
    setDeleting(true);
    try {
      const ids = g.members.map((m) => m.event_id);
      await api.post("/api/pq/events/delete", { event_ids: ids });
      const idSet = new Set(ids);
      setItems((prev) => prev.filter((e) => !idSet.has(e.event_id)));
      setSelected((s) => {
        const next = new Set(s);
        for (const id of ids) next.delete(id);
        return next;
      });
    } catch (e) {
      alert(`Delete failed: ${e}`);
    } finally {
      setDeleting(false);
    }
  }

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ windows: Window[] }>("/api/windows?include_closed=true")
      .then((r) => {
        if (cancelled) return;
        setWindows(r.windows);
        if (!windowId && r.windows.length > 0) {
          const sp2 = new URLSearchParams(sp);
          sp2.set("window", r.windows[0].window_id);
          setSp(sp2, { replace: true });
        }
      })
      .catch((e) => !cancelled && setError(String(e)));
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!windowId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);
    api
      .get<PqListResponse>(
        `/api/pq/events?window_id=${encodeURIComponent(windowId)}&limit=200`
      )
      .then((r) => {
        if (cancelled) return;
        setItems(r.items);
        setTotal(r.total);
      })
      .catch((e) => !cancelled && setError(String(e)))
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [windowId]);

  // Merge live WS events with the initial HTTP fetch. Keep HTTP as the base
  // (source of truth for history), let live events supersede by event_id.
  const displayedItems = useMemo(() => {
    const byId = new Map<string, PqEvent>();
    for (const e of items) byId.set(e.event_id, e);
    for (const [id, e] of liveEventsMap) {
      if (windowId && e.window_id && e.window_id !== windowId) continue;
      const existing = byId.get(id);
      byId.set(id, { ...existing, ...e } as PqEvent);
    }
    return Array.from(byId.values()).sort((a, b) => compareEvents(a, b, sort));
  }, [items, liveEventsMap, windowId, sort]);

  const groups = useMemo(
    () =>
      groupEvents(displayedItems).sort((a, b) =>
        compareEvents(a.latest, b.latest, sort)
      ),
    [displayedItems, sort]
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Event Feed</h1>
          <p className="text-sm text-muted-foreground">
            Chronological PQ events{IS_ADMIN_HOST ? " (all dispositions)" : " (keep + promoted)"}
          </p>
        </div>
        {windows.length > 0 && (
          <select
            value={windowId ?? ""}
            onChange={(e) => {
              const sp2 = new URLSearchParams(sp);
              sp2.set("window", e.target.value);
              setSp(sp2);
            }}
            className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          >
            <option value="" disabled>
              — select event —
            </option>
            {windows.map((w) => (
              <option key={w.window_id} value={w.window_id}>
                {w.name}
                {w.ended_at ? " (closed)" : ""}
              </option>
            ))}
          </select>
        )}
      </div>

      {error && <div className="text-destructive text-sm">Error: {error}</div>}

      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-4">
          <CardTitle>
            {loading
              ? "Loading…"
              : collapse
              ? `${groups.length} group${groups.length === 1 ? "" : "s"} · ${displayedItems.length} event${displayedItems.length === 1 ? "" : "s"}${displayedItems.length !== total ? ` (${total} stored · live)` : ""}`
              : `${displayedItems.length} event${displayedItems.length === 1 ? "" : "s"}${displayedItems.length !== total ? ` (${total} stored · live)` : ""}`}
          </CardTitle>
          <div className="flex items-center gap-3">
            {selected.size > 0 && IS_ADMIN_HOST && (
              <Button
                size="sm"
                variant="destructive"
                disabled={deleting}
                onClick={deleteSelected}
              >
                {deleting ? "Deleting…" : `Delete ${selected.size} selected`}
              </Button>
            )}
            <label className="flex items-center gap-2 text-xs text-muted-foreground cursor-pointer select-none">
              <input
                type="checkbox"
                checked={collapse}
                onChange={(e) => setCollapse(e.target.checked)}
                className="h-3.5 w-3.5 rounded border-input"
              />
              Collapse by service + type
            </label>
          </div>
        </CardHeader>
        <CardContent>
          {!loading && displayedItems.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4">
              No events in this window yet.
              {IS_ADMIN_HOST &&
                " Drops will appear here once Ship 2's MQTT disposition subscriber lands."}
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  {IS_ADMIN_HOST && <TableHead className="w-8" />}
                  <SortHeader label="Latest" sortKey="ts" sort={sort} onSort={handleSort} className="w-40" />
                  <SortHeader label="Service" sortKey="service" sort={sort} onSort={handleSort} />
                  <SortHeader label="Type" sortKey="type" sort={sort} onSort={handleSort} />
                  <SortHeader label="Decision" sortKey="decision" sort={sort} onSort={handleSort} />
                  <SortHeader label="Severity" sortKey="severity" sort={sort} onSort={handleSort} />
                  <SortHeader label="Depth / Mag %" sortKey="metric" sort={sort} onSort={handleSort} className="text-right" />
                  {IS_ADMIN_HOST && <TableHead className="w-8" />}
                </TableRow>
              </TableHeader>
              <TableBody>
                {collapse
                  ? groups.flatMap((g) => {
                      const isOpen = expandedKey === g.key;
                      const e = g.latest;
                      const groupAllSelected = g.members.every((m) => selected.has(m.event_id));
                      const rows = [
                        <TableRow
                          key={`group-${g.key}`}
                          className="cursor-pointer"
                          onClick={() => {
                            if (g.count > 1) {
                              setExpandedKey(isOpen ? null : g.key);
                            } else {
                              navigate(`/events/${encodeURIComponent(e.event_id)}?window=${windowId}`);
                            }
                          }}
                        >
                          {IS_ADMIN_HOST && (
                            <TableCell className="w-8" onClick={(ev) => ev.stopPropagation()}>
                              <input
                                type="checkbox"
                                checked={groupAllSelected}
                                onChange={() => toggleSelectGroup(g)}
                                className="h-4 w-4 rounded border-input"
                                title={`Select all ${g.count} in group`}
                              />
                            </TableCell>
                          )}
                          <TableCell className="tabular-nums text-xs">
                            <span className="inline-flex items-center gap-1.5">
                              {g.count > 1 && (
                                <span
                                  className="inline-block text-muted-foreground transition-transform"
                                  style={{ transform: isOpen ? "rotate(90deg)" : undefined }}
                                  aria-hidden
                                >
                                  ▸
                                </span>
                              )}
                              {formatTs(e.trigger_ts)}
                            </span>
                          </TableCell>
                          <TableCell>
                            <div className="font-medium flex items-center gap-2">
                              {e.service_name ?? (
                                <span className="text-muted-foreground">(unnamed)</span>
                              )}
                              {g.count > 1 && (
                                <span className="rounded-md border border-border bg-muted px-1.5 py-0.5 text-xs font-mono">
                                  ×{g.count}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground font-mono">
                              {e.system_id}
                            </div>
                          </TableCell>
                          <TableCell className="text-xs">{e.event_type}</TableCell>
                          <TableCell>
                            <DecisionBadge decision={e.decision ?? "keep"} />
                          </TableCell>
                          <TableCell>
                            <SeverityBadge severity={e.severity} />
                          </TableCell>
                          <TableCell className="text-right tabular-nums text-xs">
                            {e.depth_pct != null ? `${e.depth_pct.toFixed(1)}%` : ""}
                            {e.depth_pct != null && e.magnitude_pct != null && " / "}
                            {e.magnitude_pct != null ? `${e.magnitude_pct.toFixed(1)}%` : ""}
                            {e.depth_pct == null && e.magnitude_pct == null && "—"}
                          </TableCell>
                          {IS_ADMIN_HOST && (
                            <TableCell className="w-8 text-right" onClick={(ev) => ev.stopPropagation()}>
                              <button
                                type="button"
                                onClick={() => (g.count > 1 ? deleteGroup(g) : deleteOne(e.event_id))}
                                className="text-muted-foreground hover:text-destructive text-sm"
                                title={g.count > 1 ? `Delete all ${g.count} in group` : "Delete event"}
                                disabled={deleting}
                              >
                                ✕
                              </button>
                            </TableCell>
                          )}
                        </TableRow>,
                      ];
                      if (isOpen && g.count > 1) {
                        const sortedMembers = [...g.members].sort((a, b) =>
                          (b.trigger_ts ?? "").localeCompare(a.trigger_ts ?? "")
                        );
                        for (const m of sortedMembers) {
                          rows.push(
                            <TableRow
                              key={`member-${m.event_id}`}
                              className="cursor-pointer bg-muted/30"
                              onClick={() =>
                                navigate(`/events/${encodeURIComponent(m.event_id)}?window=${windowId}`)
                              }
                            >
                              {IS_ADMIN_HOST && (
                                <TableCell className="w-8 pl-8" onClick={(ev) => ev.stopPropagation()}>
                                  <input
                                    type="checkbox"
                                    checked={selected.has(m.event_id)}
                                    onChange={() => toggleSelect(m.event_id)}
                                    className="h-4 w-4 rounded border-input"
                                  />
                                </TableCell>
                              )}
                              <TableCell className="tabular-nums text-xs text-muted-foreground">
                                {formatTs(m.trigger_ts)}
                              </TableCell>
                              <TableCell className="text-xs font-mono text-muted-foreground">
                                {m.event_id.replace(/^cloud:/, "").slice(0, 12)}…
                              </TableCell>
                              <TableCell className="text-xs">{m.event_type}</TableCell>
                              <TableCell>
                                <DecisionBadge decision={m.decision ?? "keep"} />
                              </TableCell>
                              <TableCell>
                                <SeverityBadge severity={m.severity} />
                              </TableCell>
                              <TableCell className="text-right tabular-nums text-xs">
                                {m.depth_pct != null ? `${m.depth_pct.toFixed(1)}%` : ""}
                                {m.depth_pct != null && m.magnitude_pct != null && " / "}
                                {m.magnitude_pct != null ? `${m.magnitude_pct.toFixed(1)}%` : ""}
                                {m.depth_pct == null && m.magnitude_pct == null && "—"}
                              </TableCell>
                              {IS_ADMIN_HOST && (
                                <TableCell className="w-8 text-right" onClick={(ev) => ev.stopPropagation()}>
                                  <button
                                    type="button"
                                    onClick={() => deleteOne(m.event_id)}
                                    className="text-muted-foreground hover:text-destructive text-sm"
                                    title="Delete event"
                                    disabled={deleting}
                                  >
                                    ✕
                                  </button>
                                </TableCell>
                              )}
                            </TableRow>
                          );
                        }
                      }
                      return rows;
                    })
                  : displayedItems.map((e) => (
                      <TableRow
                        key={e.event_id}
                        className="cursor-pointer"
                        onClick={() =>
                          navigate(`/events/${encodeURIComponent(e.event_id)}?window=${windowId}`)
                        }
                      >
                        {IS_ADMIN_HOST && (
                          <TableCell className="w-8" onClick={(ev) => ev.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={selected.has(e.event_id)}
                              onChange={() => toggleSelect(e.event_id)}
                              className="h-4 w-4 rounded border-input"
                            />
                          </TableCell>
                        )}
                        <TableCell className="tabular-nums text-xs">
                          {formatTs(e.trigger_ts)}
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {e.service_name ?? (
                              <span className="text-muted-foreground">(unnamed)</span>
                            )}
                          </div>
                          <div className="text-xs text-muted-foreground font-mono">
                            {e.system_id}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs">{e.event_type}</TableCell>
                        <TableCell>
                          <DecisionBadge decision={e.decision ?? "keep"} />
                        </TableCell>
                        <TableCell>
                          <SeverityBadge severity={e.severity} />
                        </TableCell>
                        <TableCell className="text-right tabular-nums text-xs">
                          {e.depth_pct != null ? `${e.depth_pct.toFixed(1)}%` : ""}
                          {e.depth_pct != null && e.magnitude_pct != null && " / "}
                          {e.magnitude_pct != null ? `${e.magnitude_pct.toFixed(1)}%` : ""}
                          {e.depth_pct == null && e.magnitude_pct == null && "—"}
                        </TableCell>
                        {IS_ADMIN_HOST && (
                          <TableCell className="w-8 text-right" onClick={(ev) => ev.stopPropagation()}>
                            <button
                              type="button"
                              onClick={() => deleteOne(e.event_id)}
                              className="text-muted-foreground hover:text-destructive text-sm"
                              title="Delete event"
                              disabled={deleting}
                            >
                              ✕
                            </button>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
